import BillingMessageBox from '@/components/common/message-box';
import { translation } from '@/plugins/i18n';
import { isEmptyVal, safeJSONParse } from '@/utils';
import tracking from '@/aspects/trackings';
import _ from 'lodash';
import store from '@/store';
import { logger } from '@/utils/log';
import { pageConfig } from '@/common/service/page-config';
import { getPartnerRefer, getTrackingPlanNames } from '@/views/config/partner-refer/helper/utils';
import {
  messageRuleCodeMap,
  partnerRuleErrorCodeMap,
} from '@/views/config/partner-refer/helper/constant';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { checkPartnerForAbTest } from '@/views/config/partner-refer/helper/message';

const checkPartnerForSoldToCountry = (soldToCountry, ruleList = []) => {
  if (isEmptyVal(ruleList) || isEmptyVal(soldToCountry) || (soldToCountry && _.includes(ruleList, soldToCountry))) {
    return true;
  }
};

const checkPartnerForIpCountry = (ruleList = []) => {
  const getters = store.getters;
  const ipCountry = _.upperCase(getters.ipLocation?.cn); // from ip location country code

  if (isEmptyVal(ruleList) || _.includes(ruleList, ipCountry)) {
    return true;
  }
};

const checkPartnerForAccountType = (ruleList = []) => {
  if (isEmptyVal(ruleList) || _.includes(ruleList, pageConfig?.accountType)) {
    return true;
  }
};

const checkPartnerForZmCoupon = () => {
  const getters = store.getters;
  const partnerRefer = getPartnerRefer();
  const cartCouponCode = _.upperCase(getters.couponState?.couponCode);
  const partnerCouponCode = _.upperCase(partnerRefer.couponCode);

  // not bind partner with coupon
  if (isEmptyVal(partnerCouponCode)) {
    return true;
  }

  // shopping cart not have coupon code
  // case one: When the partner coupon is removed, it will also be ineligible
  if (isEmptyVal(cartCouponCode)) {
    return false;
  }

  // case two: When the coupon applied is inconsistent with the coupon bound to the partner, the partner refer will ineligible.
  if (partnerCouponCode !== cartCouponCode) {
    return false;
  }

  return true;
};

export const popUpMessage = async (options = {}) => {
  const { generalErrorMsg, blockOfferModalContentMsg, partnerName } = getPartnerRefer();
  const { originPromoProductName = '' } = options;
  const promoPartnerName = `partner promo - ${partnerName ?? 'partner name'}`;
  const message = getMessageTemplateByRules(blockOfferModalContentMsg)?.messageBody
    ?? getMessageTemplateByRules(generalErrorMsg)?.messageBody;

  tracking.buyInteraction(
    promoPartnerName,
    `${originPromoProductName} - offer no longer applies modal - impression`,
  );

  return BillingMessageBox.confirm(message, translation('billing.partner.offer_no_longer_applies_title'), {
    confirmButtonText: translation('billing.partner.proceed_without_offer'),
    cancelButtonText: translation('billing.common.cancel'),
    customClass: 'partner-refer-message-popup',
    dangerouslyUseHTMLString: true,
  }).catch(() => {
    logger.error('cancel execute for with partner offer');
    tracking.buyInteraction(
      promoPartnerName,
      `${originPromoProductName} - offer no longer applies modal - cancel`,
    );
    return Promise.reject();
  }).then(() => {
    tracking.buyInteraction(
      promoPartnerName,
      `${originPromoProductName} - offer no longer applies modal - proceed without offer`,
    );
    return Promise.resolve();
  });
};

/**
 * When the user changes the shopping cart and the verification fails by calling the backend interface, this pop-up modal needs to be displayed.
 * @param options
 * @return {Promise<undefined|Promise<unknown>>}
 */
export const popUpDialogForAfterValidate = async (options = {}) => {
  const { generalErrorMsg, blockOfferModalContentMsg, blockOfferModalContentTitle, partnerName } = getPartnerRefer();
  const { originPromoProductName = '' } = options;
  const promoPartnerName = `partner promo - ${partnerName ?? 'partner name'}`;

  const message = getMessageTemplateByRules(blockOfferModalContentMsg)?.messageBody
    ?? getMessageTemplateByRules(generalErrorMsg)?.messageBody;

  const defaultTitle = translation('billing.partner.offer_no_longer_applies_title');
  const title = getMessageTemplateByRules(blockOfferModalContentTitle)?.messageBody ?? defaultTitle;

  tracking.buyInteraction(
    promoPartnerName,
    `${originPromoProductName} - offer no longer applies modal - impression`,
  );

  return BillingMessageBox.alert(message, title, {
    confirmButtonText: translation('common.btn_ok'),
    customClass: 'partner-refer-message-popup',
    dangerouslyUseHTMLString: true,
  });
};

/**
 * @description check current action for eligible plan list, and return selected planCode
 * @param oldPlanList
 * @param newPlanList
 * @return {array|undefined}
 */
export const getEligiblePlanCodeListForPartner = (
  oldPlanList = [],
  newPlanList = [],
) => {
  const partnerRefer = getPartnerRefer();

  if (isEmptyVal(partnerRefer)) return;
  if (isEmptyVal(partnerRefer.rules?.eligiblePlanList)) return;

  // current shopping cart exist selected plan list
  let selectedPlanMap = { ...store.getters?.getSelectedPlansWithQuantityMap };

  // remove old plan
  if (oldPlanList?.length) {
    selectedPlanMap = _.omitBy(selectedPlanMap, (value, planCode) => {
      return _.some(oldPlanList, (p) => p.plan_code === planCode);
    });
  }

  // add new plan
  if (newPlanList?.length) {
    selectedPlanMap = _.reduce(newPlanList, (pre, plan) => {
      return {
        ...pre,
        [plan?.plan_code]: plan?.cartInfo?.selected_number,
      };
    }, selectedPlanMap);
  }

  return _.find(partnerRefer.rules?.eligiblePlanList, (list = []) => {
    return _.every(list, (p) => _.has(selectedPlanMap, p));
  });
};

const checkEligibleForPartnerByCouponValidation = () => {
  if (store.getters.verifyCouponStatus === couponFetchStatus.failure) {
    logger.error('[coupon] partner for promotion coupon validate fail');
    return partnerRuleErrorCodeMap.promotionBindCouponValidFail;
  }

  logger.info('[coupon] eligible for partner bind with coupon refer!!!');
};

const checkEligibleForPartnerByReferralCouponValidation = () => {
  if (store.getters.referralCouponStatus === couponFetchStatus.failure) {
    logger.error('[referral coupon] partner for promotion referral coupon validate fail');
    return partnerRuleErrorCodeMap.promotionBindCouponValidFail;
  }

  logger.info('[referral coupon] eligible for partner bind with coupon!!!');
};

/**
 * @description check partner refer
 * @param eligiblePlanCodeListForPartner
 * @param soldToCountry
 * @return {Object|undefined}
 */
export const checkEligibleForPartner = (eligiblePlanCodeListForPartner, soldToCountry) => {
  const partnerRefer = getPartnerRefer();
  const getters = store.getters;

  // when the partner refer pre check fail from backend in init step, Skip verification directly
  if (getters.ignoreCheckPartner) {
    return partnerRuleErrorCodeMap.preCheckWithPartnerFail;
  }

  if (isEmptyVal(partnerRefer)) {
    return partnerRuleErrorCodeMap.notPartnerRefer;
  }

  /// first check coupon (compare with partner coupon and the shopping cart entered a coupon)
  if (partnerRefer?.couponCode) {
    if (!checkPartnerForZmCoupon()) {
      logger.error('partner for promotion coupon not match shopping cart enter a couponCode');
      return partnerRuleErrorCodeMap.promotionCouponMatch;
    }
  }

  // todo partner by arvin Currently, the verification logic of coupon is only determined based on whether the partner contains a coupon. Hard code believes that partner and coupon are strongly bound.
  // validate partner with coupon, partner bind with coupon, validate cover by coupon service validation
  if (partnerRefer?.couponCode && partnerRefer?.rules?.validateByCoupon) {
    return checkEligibleForPartnerByCouponValidation();
  }

  // use coupon service to validate partner referral (fake coupon, just validate, not redeem this fake coupon)
  if (partnerRefer?.referralCouponCode && partnerRefer?.rules?.validateByReferralCoupon) {
    return checkEligibleForPartnerByReferralCouponValidation();
  }

  // validate partner without coupon
  const eligibleAccountTypeList = partnerRefer.rules?.eligibleAccountType || []; // partner check rules
  const eligibleCountryList = _.map(partnerRefer.rules?.eligibleCountry, (p) => _.upperCase(p)); // eligible country list
  soldToCountry = _.upperCase(soldToCountry ?? getters.soldToContact?.country); // sold to contact country code
  const eligiblePlanList = partnerRefer.rules?.eligiblePlanList;

  // when the soldTo country is empty, will valid ip address
  if (isEmptyVal(soldToCountry) && !checkPartnerForIpCountry(eligibleCountryList)) {
    logger.error('partner for ip is not in support country list');
    return partnerRuleErrorCodeMap.ipLocationCountry;
  }

  if (!checkPartnerForSoldToCountry(soldToCountry, eligibleCountryList)) {
    logger.error('partner for sold to country is not in support country list');
    return partnerRuleErrorCodeMap.soldToCountry;
  }

  if (!checkPartnerForAccountType(eligibleAccountTypeList)) {
    logger.error('partner for accountType invalid');
    return partnerRuleErrorCodeMap.accountType;
  }

  // when the check rules not config eligible product list, will jump eligible plan check
  if (!isEmptyVal(eligiblePlanList) && isEmptyVal(eligiblePlanCodeListForPartner)) {
    logger.error('partner for current selected plans not match promotion plan list');
    return partnerRuleErrorCodeMap.promotionPlanMatch;
  }

  logger.info('eligible for partner refer!!!');
  return undefined;
};

/**
 * @description Check whether the partner and zoom coupon can be used together, zoom coupon priority is higher than Partner refer (when allowUseZMCoupon is false)
 * @param couponState
 * @return {boolean} true -> allow, false -> not allow
 */
export const checkPartnerReferWithZmCoupon = (couponState = {}) => {
  // partner refer not allowed to use with zoom coupon
  const { allowUseZMCoupon = false } = getPartnerRefer().rules;

  // not allow with zoom coupon, should return false
  if (!allowUseZMCoupon && couponState?.couponCode) {
    return false;
  }

  return true;
};

/**
 * @description pre check partner refer for switch plan
 * @param oldPlanList
 * @param newPlanList
 * @param soldToCountry
 * @return {Promise<void|*>}
 */
export const preCheckPartnerRefer = async (
  oldPlanList = [],
  newPlanList = [],
  soldToCountry,
) => {
  const partnerRefer = getPartnerRefer();

  // control by rules or error message
  if (isEmptyVal(partnerRefer) || partnerRefer?.rules?.hideBlockOfferModalSwitch) {
    return Promise.resolve();
  }

  if (store.getters.ignoreCheckPartner) {
    return Promise.resolve();
  }

  oldPlanList = oldPlanList?.filter(Boolean);
  newPlanList = newPlanList?.filter(Boolean);

  const { couponState, eligiblePartnerReferOffer: prevEligiblePartnerReferOffer, groupByPlanCodePlanMap } = store.getters;
  const eligiblePlanCodeListForPartner = getEligiblePlanCodeListForPartner(oldPlanList, newPlanList);

  const nextEligibleForPartnerValidObject = checkEligibleForPartner(eligiblePlanCodeListForPartner, soldToCountry);
  const nextIsEligibleForPartner = isEmptyVal(nextEligibleForPartnerValidObject);
  // The customer use a zoom coupon code, even if the customer eligible for partner refer,
  const checkWithZmCouponResult = checkPartnerReferWithZmCoupon(couponState);
  const nextEligiblePartnerReferOffer = nextIsEligibleForPartner && checkWithZmCouponResult;

  const originPromoProductName = getTrackingPlanNames(_.map(oldPlanList, (p) => p?.plan_code), groupByPlanCodePlanMap);

  // case 1: prev partner refer ineligible, next partner refer ineligible (not pop up modal)
  // case 2: prev partner refer ineligible, next partner refer eligible (not pop up modal)
  // case 3: prev partner refer eligible, next partner refer ineligible (pop up modal)
  if (prevEligiblePartnerReferOffer && !nextEligiblePartnerReferOffer) {
    return popUpMessage({ originPromoProductName });
  }

  return Promise.resolve();
};

/**
 * @description check partner refer message rules
 * @param messageRuleList {MessageRuleBean[]|any[]}
 * @param soldToCountry {string|undefined}
 * @param planCodeList --> eligiblePlanCodeListForPartner
 * @return {boolean}
 */
export const checkPartnerMsgByCondition = (messageRuleList = [], {
  soldToCountry = undefined,
  planCodeList = [],
}) => {
  // PRO INCREASE
  planCodeList = _.reduce(planCodeList, (pre, planCode) => {
    const matchList = PLAN_PRICE_CHANGE_UTILS.getPlanListForPriceChange(planCode);
    return matchList ? [...pre, ...matchList] : [...pre, planCode];
  }, []);

  // not have any message rule, should return true
  if (isEmptyVal(messageRuleList)) return true;

  return _.every(messageRuleList, (condition) => {
    let { ruleCode, ruleValue } = condition;

    if (ruleCode === messageRuleCodeMap.abTest) {
      const ruleList = _.split(ruleValue, ',');
      return checkPartnerForAbTest(ruleList);
    }

    if (_.isString(ruleValue)) {
      ruleValue = safeJSONParse(ruleValue, []);
      logger.info('parse ruleValue result', ruleCode, ruleValue);
    }

    if (ruleCode === messageRuleCodeMap.accountType) {
      return checkPartnerForAccountType(ruleValue);
    }

    if (ruleCode === messageRuleCodeMap.country) {
      // sold to contact country code
      soldToCountry = _.upperCase(soldToCountry ?? store?.getters.soldToContact?.country);

      return isEmptyVal(soldToCountry) ?
        checkPartnerForIpCountry(ruleValue) :
        checkPartnerForSoldToCountry(soldToCountry, ruleValue);
    }

    if (ruleCode === messageRuleCodeMap.planList && !isEmptyVal(ruleValue)) {
      // ex: ruleValue: [['yearly_c50', 'master_class_yearly']] means every ruleValue item can be found in the shopping cart
      return _.some(ruleValue, (list) => _.every(list, (p) => _.includes(planCodeList, p)));
    }

    return true;
  });
};

/**
 *
 * @param message {Array<OfferDetailRuleBean>|string}
 * @return {OfferDetailRuleBean}
 */
export const getMessageTemplateByRules = (message) => {
  if (_.isArray(message)) {
    const planCodeList = _.keys(store.getters.getSelectedPlansWithQuantityMap);

    return _.find(message, (p) => {
      return checkPartnerMsgByCondition(p?.messageRuleList, { planCodeList });
    }) || {};
  }

  if (_.isString(message)) {
    return { messageBody: message, messageRuleList: [] };
  }

  return {};
};

/**
 *
 * @param message {Array<OfferDetailRuleBean>|string}
 * @return {string}
 */
export const getMessageStringByRules = (message) => {
  return getMessageTemplateByRules(message)?.messageBody;
}

// remove partner bind coupon in the shopping cart
export const popUpModalByRemoveCoupon = async (cartCouponCode) => {
  const partnerRefer = getPartnerRefer();
  const partnerBindCoupon = partnerRefer?.couponCode;

  if (store.getters.ignoreCheckPartner) {
    return Promise.resolve();
  }

  if (
    partnerRefer?.couponCode
    && partnerRefer?.rules?.popUpModalForRemoveCoupon
    && cartCouponCode === partnerBindCoupon
  ) {
    const title = getMessageTemplateByRules(partnerRefer?.couponRemovedTitle)?.messageBody;
    const message = getMessageTemplateByRules(partnerRefer?.couponRemovedDesc)?.messageBody;

    if (title && message) {
      const promoPartnerName = `partner promo - ${partnerRefer?.partnerName}`;
      const partnerReferProductNames = store.getters?.partnerReferProductNames;
      const removeModalName = `${partnerReferProductNames} - offer has been removed modal`;

      tracking.buyInteraction(
        promoPartnerName,
        `${removeModalName} - impression`,
      );

      return BillingMessageBox.confirm(message, title, {
        confirmButtonText: translation('billing.partner.proceed_without_offer'),
        cancelButtonText: translation('billing.common.cancel'),
        customClass: 'partner-refer-message-popup',
      }).catch(() => {
        logger.error('cancel execute for with partner offer');
        tracking.buyInteraction(
          promoPartnerName,
          `${removeModalName} - cancel`,
        );
        return Promise.reject();
      }).then(() => {
        tracking.buyInteraction(
          promoPartnerName,
          `${removeModalName} - proceed without offer`,
        );
        return Promise.resolve();
      });
      ;
    }
  }

  return Promise.resolve();
};
